/**
 * InvoiceBuilder.tsx
 * Full invoice builder — customer search, line item editor,
 * net terms, template loader, save as draft or create & send.
 */
import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

// ─── Types ────────────────────────────────────────────────────────────────────

interface StripeCustomer {
  id: string;
  name: string;
  email: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description?: string;
}

interface StripePrice {
  id: string;
  productId: string;
  nickname?: string;
  unitAmount: number; // cents
  currency: string;
}

interface LineItem {
  id: string; // local uuid for React key
  description: string;
  quantity: number;
  unitPrice: number; // dollars
}

interface BillingTemplate {
  id: number;
  name: string;
  netTerms: number;
  footer: string;
  lineItems: Array<{ description: string; quantity: number; amount: number }>;
}

interface Props {
  onClose: () => void;
  onCreated: (invoice: unknown) => void;
}

const NET_TERMS = [
  { value: 0, label: "Due on Receipt" },
  { value: 7, label: "Net 7" },
  { value: 15, label: "Net 15" },
  { value: 30, label: "Net 30" },
  { value: 45, label: "Net 45" },
  { value: 60, label: "Net 60" },
];

function formatCurrency(dollars: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(dollars);
}

function uuid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ─── Customer Search Combobox ─────────────────────────────────────────────────

function CustomerPicker({
  api,
  value,
  onChange,
}: {
  api: ReturnType<typeof useApi>["admin"];
  value: StripeCustomer | null;
  onChange: (c: StripeCustomer | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<StripeCustomer[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputClass =
    "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  const search = useCallback(
    async (q: string) => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const resp = await api.get(`api/StripeConfig/customers?q=${encodeURIComponent(q)}`);
        if (resp.ok) {
          const data = await resp.json();
          setResults(Array.isArray(data) ? data : []);
        }
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [api]
  );

  function handleQueryChange(q: string) {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 350);
  }

  async function handleCreate() {
    if (!newName || !newEmail) return;
    setCreating(true);
    try {
      const resp = await api.post("api/StripeConfig/customers", {
        name: newName,
        email: newEmail,
        company: newCompany,
        phone: newPhone,
      });
      if (resp.ok) {
        const cust: StripeCustomer = await resp.json();
        onChange(cust);
        setShowCreate(false);
        setOpen(false);
        setQuery(cust.name);
      }
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  }

  if (value) {
    return (
      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <div>
          <p className="text-sm font-medium text-slate-800">{value.name}</p>
          <p className="text-xs text-slate-500">{value.email}</p>
        </div>
        <button
          className="text-xs text-slate-400 hover:text-slate-600"
          onClick={() => { onChange(null); setQuery(""); }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className={inputClass}
        placeholder="Search customers by name or email…"
        value={query}
        onChange={(e) => handleQueryChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          {searching && (
            <div className="px-3 py-2 text-xs text-slate-400">
              <i className="fa-solid fa-spinner fa-spin mr-1" /> Searching…
            </div>
          )}
          {!searching && results.length === 0 && query.length > 1 && (
            <div className="px-3 py-2 text-xs text-slate-400">No customers found.</div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              onMouseDown={() => { onChange(c); setQuery(c.name); setOpen(false); }}
            >
              <p className="text-sm font-medium text-slate-800">{c.name}</p>
              <p className="text-xs text-slate-500">{c.email}</p>
            </button>
          ))}
          <button
            className="w-full text-left px-3 py-2 text-xs text-cornflower-blue-600 hover:bg-slate-50 flex items-center gap-1"
            onMouseDown={() => setShowCreate(true)}
          >
            <i className="fa-solid fa-plus" /> New Customer
          </button>
        </div>
      )}

      {/* Create Customer Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800">New Customer</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>Full Name *</label>
                <input type="text" className={inputClass} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Jane Smith" />
              </div>
              <div>
                <label className={labelClass}>Email *</label>
                <input type="email" className={inputClass} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="jane@company.com" />
              </div>
              <div>
                <label className={labelClass}>Company</label>
                <input type="text" className={inputClass} value={newCompany} onChange={(e) => setNewCompany(e.target.value)} placeholder="Acme Corp" />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input type="tel" className={inputClass} value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+1 555-000-0000" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleCreate}
                disabled={creating || !newName || !newEmail}
                className="flex-1 px-4 py-2 bg-cornflower-blue-600 text-white text-sm rounded-md hover:bg-cornflower-blue-700 disabled:opacity-50"
              >
                {creating ? "Creating…" : "Create Customer"}
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Product/Price Picker Modal ───────────────────────────────────────────────

function ProductPicker({
  api,
  onSelect,
  onClose,
}: {
  api: ReturnType<typeof useApi>["admin"];
  onSelect: (item: LineItem) => void;
  onClose: () => void;
}) {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<StripeProduct | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);

  useEffect(() => {
    api.get("api/StripeConfig/products").then(async (resp) => {
      if (resp.ok) {
        const data = await resp.json();
        setProducts(Array.isArray(data) ? data : []);
      }
    }).catch(() => {}).finally(() => setLoadingProducts(false));
  }, [api]);

  async function handleSelectProduct(product: StripeProduct) {
    setSelectedProduct(product);
    setPrices([]);
    setLoadingPrices(true);
    try {
      const resp = await api.get(`api/StripeConfig/prices?productId=${product.id}`);
      if (resp.ok) {
        const data = await resp.json();
        setPrices(Array.isArray(data) ? data : []);
      }
    } catch {
      setPrices([]);
    } finally {
      setLoadingPrices(false);
    }
  }

  function handleSelectPrice(price: StripePrice) {
    onSelect({
      id: uuid(),
      description: selectedProduct?.name ?? price.nickname ?? "Product",
      quantity: 1,
      unitPrice: price.unitAmount / 100,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between shrink-0">
          <h3 className="text-base font-semibold text-slate-800">Add from Catalog</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="flex gap-4 flex-1 min-h-0">
          {/* Products list */}
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
            {loadingProducts ? (
              <div className="p-3 text-xs text-slate-400">
                <i className="fa-solid fa-spinner fa-spin mr-1" /> Loading…
              </div>
            ) : products.length === 0 ? (
              <div className="p-3 text-xs text-slate-400">No products found in Stripe.</div>
            ) : (
              products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelectProduct(p)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                    selectedProduct?.id === p.id ? "bg-cornflower-blue-50 text-cornflower-blue-700" : ""
                  }`}
                >
                  <p className="text-sm font-medium">{p.name}</p>
                  {p.description && <p className="text-xs text-slate-400 truncate">{p.description}</p>}
                </button>
              ))
            )}
          </div>

          {/* Prices list */}
          {selectedProduct && (
            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
              {loadingPrices ? (
                <div className="p-3 text-xs text-slate-400">
                  <i className="fa-solid fa-spinner fa-spin mr-1" /> Loading prices…
                </div>
              ) : prices.length === 0 ? (
                <div className="p-3 text-xs text-slate-400">No prices for this product.</div>
              ) : (
                prices.map((pr) => (
                  <button
                    key={pr.id}
                    onClick={() => handleSelectPrice(pr)}
                    className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  >
                    <p className="text-sm font-medium">
                      {formatCurrency(pr.unitAmount / 100)}
                    </p>
                    {pr.nickname && <p className="text-xs text-slate-400">{pr.nickname}</p>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InvoiceBuilder({ onClose, onCreated }: Props) {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [customer, setCustomer] = useState<StripeCustomer | null>(null);
  const [netTerms, setNetTerms] = useState(30);
  const [customDays, setCustomDays] = useState("");
  const [description, setDescription] = useState("");
  const [footer, setFooter] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: uuid(), description: "", quantity: 1, unitPrice: 0 },
  ]);

  const [templates, setTemplates] = useState<BillingTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");

  const [showProductPicker, setShowProductPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState<"draft" | "send">("draft");

  const inputClass =
    "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500 bg-white";
  const labelClass = "block text-sm font-medium text-slate-700 mb-1";

  // Load templates
  useEffect(() => {
    api.admin.get("api/BillingTemplate").then(async (resp) => {
      if (resp.ok) {
        const data = await resp.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    }).catch(() => {});
  }, [api.admin]);

  function handleLoadTemplate(templateId: number) {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    setNetTerms(tpl.netTerms);
    setFooter(tpl.footer ?? "");
    setLineItems(
      (tpl.lineItems ?? []).map((li) => ({
        id: uuid(),
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.amount / 100,
      }))
    );
    setSelectedTemplateId(templateId);
  }

  function updateLineItem(id: string, field: keyof LineItem, value: string | number) {
    setLineItems((prev) =>
      prev.map((li) => (li.id === id ? { ...li, [field]: value } : li))
    );
  }

  function removeLineItem(id: string) {
    setLineItems((prev) => prev.filter((li) => li.id !== id));
  }

  function addBlankLine() {
    setLineItems((prev) => [...prev, { id: uuid(), description: "", quantity: 1, unitPrice: 0 }]);
  }

  const subtotal = lineItems.reduce((sum, li) => sum + li.quantity * li.unitPrice, 0);

  const effectiveDays =
    netTerms === -1
      ? parseInt(customDays, 10) || 0
      : netTerms;

  async function handleSubmit(mode: "draft" | "send") {
    if (!customer) {
      toastAlert.displayToast(Severity.Warning, "Please select a customer first.");
      return;
    }
    if (lineItems.length === 0 || lineItems.every((li) => !li.description)) {
      toastAlert.displayToast(Severity.Warning, "Add at least one line item.");
      return;
    }
    setSubmitMode(mode);
    setSubmitting(true);
    try {
      // 1. Create invoice
      const createResp = await api.admin.post("api/StripeInvoice", {
        customerId: customer.id,
        daysUntilDue: effectiveDays,
        description,
        footer,
        lineItems: lineItems
          .filter((li) => li.description)
          .map((li) => ({
            description: li.description,
            amount: Math.round(li.unitPrice * 100),
            quantity: li.quantity,
          })),
      });

      if (!createResp.ok) {
        const err = await createResp.json().catch(() => ({ message: "Failed to create invoice." }));
        toastAlert.displayToast(Severity.Error, err.message || "Failed to create invoice.");
        return;
      }

      const invoice = await createResp.json();
      const invoiceId = invoice.id;

      // 2. Finalize
      const finalizeResp = await api.admin.post(`api/StripeInvoice/${invoiceId}/finalize`, {});
      if (!finalizeResp.ok) {
        toastAlert.displayToast(Severity.Warning, "Invoice created but could not be finalized.");
        onCreated(invoice);
        onClose();
        return;
      }

      // 3. Send (if mode = send)
      if (mode === "send") {
        const sendResp = await api.admin.post(`api/StripeInvoice/${invoiceId}/send`, {});
        if (sendResp.ok) {
          toastAlert.displayToast(Severity.Success, "Invoice created and sent successfully.");
        } else {
          toastAlert.displayToast(Severity.Warning, "Invoice finalized but could not send email.");
        }
      } else {
        toastAlert.displayToast(Severity.Success, "Invoice saved as draft.");
      }

      onCreated(invoice);
      onClose();
    } catch {
      toastAlert.displayToast(Severity.Error, "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex items-end justify-end">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-lg font-semibold text-slate-800">New Invoice</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 1. Customer */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Customer</h3>
            <CustomerPicker api={api.admin} value={customer} onChange={setCustomer} />
          </section>

          {/* 2. Template loader */}
          {templates.length > 0 && (
            <section>
              <label className={labelClass}>Load from Template</label>
              <select
                className={inputClass}
                value={selectedTemplateId}
                onChange={(e) => {
                  const id = parseInt(e.target.value, 10);
                  if (!isNaN(id)) handleLoadTemplate(id);
                  else setSelectedTemplateId("");
                }}
              >
                <option value="">— Select template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.lineItems?.length ?? 0} items)
                  </option>
                ))}
              </select>
            </section>
          )}

          {/* 3. Invoice Settings */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Invoice Settings</h3>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Net Terms</label>
                <select
                  className={inputClass}
                  value={netTerms}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setNetTerms(v);
                  }}
                >
                  {NET_TERMS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                  <option value={-1}>Custom</option>
                </select>
                {netTerms === -1 && (
                  <input
                    type="number"
                    className={`${inputClass} mt-2`}
                    placeholder="Days until due"
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                    min="1"
                  />
                )}
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  placeholder="Invoice description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Footer Notes</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  placeholder="Payment instructions, thank-you notes, etc."
                  value={footer}
                  onChange={(e) => setFooter(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* 4. Line Items */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Line Items</h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_80px_110px_90px_32px] gap-2 bg-slate-50 px-3 py-2 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
                <span>Description</span>
                <span>Qty</span>
                <span>Unit Price</span>
                <span className="text-right">Total</span>
                <span />
              </div>
              {/* Rows */}
              <div className="divide-y divide-slate-100">
                {lineItems.map((li) => (
                  <div key={li.id} className="grid grid-cols-[1fr_80px_110px_90px_32px] gap-2 px-3 py-2 items-center">
                    <input
                      type="text"
                      className="text-sm border-0 outline-none bg-transparent w-full"
                      placeholder="Item description"
                      value={li.description}
                      onChange={(e) => updateLineItem(li.id, "description", e.target.value)}
                    />
                    <input
                      type="number"
                      className="text-sm border border-slate-200 rounded px-2 py-1 w-full text-center"
                      min="1"
                      value={li.quantity}
                      onChange={(e) => updateLineItem(li.id, "quantity", parseFloat(e.target.value) || 1)}
                    />
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        className="text-sm border border-slate-200 rounded px-2 py-1 pl-5 w-full"
                        min="0"
                        step="0.01"
                        value={li.unitPrice}
                        onChange={(e) => updateLineItem(li.id, "unitPrice", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <span className="text-sm text-slate-800 text-right tabular-nums">
                      {formatCurrency(li.quantity * li.unitPrice)}
                    </span>
                    <button
                      onClick={() => removeLineItem(li.id)}
                      className="text-slate-300 hover:text-red-400 text-sm"
                      aria-label="Remove line item"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                ))}
              </div>
              {/* Add buttons */}
              <div className="px-3 py-2 border-t border-slate-100 flex gap-3">
                <button
                  onClick={addBlankLine}
                  className="text-xs text-cornflower-blue-600 hover:underline flex items-center gap-1"
                >
                  <i className="fa-solid fa-plus" /> Add blank line
                </button>
                <button
                  onClick={() => setShowProductPicker(true)}
                  className="text-xs text-cornflower-blue-600 hover:underline flex items-center gap-1"
                >
                  <i className="fa-solid fa-magnifying-glass" /> Add from catalog
                </button>
              </div>
            </div>

            {/* Totals */}
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-400 text-xs">
                <span>Tax</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-800 border-t border-slate-200 pt-2 mt-1">
                <span>Total</span>
                <span className="tabular-nums">{formatCurrency(subtotal)}</span>
              </div>
            </div>
          </section>
        </div>

        {/* Actions footer */}
        <div className="shrink-0 border-t border-slate-200 px-6 py-4 flex gap-3 bg-white">
          <button
            onClick={() => handleSubmit("draft")}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && submitMode === "draft" && <i className="fa-solid fa-spinner fa-spin" />}
            Save as Draft
          </button>
          <button
            onClick={() => handleSubmit("send")}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && submitMode === "send" && <i className="fa-solid fa-spinner fa-spin" />}
            Create &amp; Send
          </button>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
            Cancel
          </button>
        </div>
      </div>

      {showProductPicker && (
        <ProductPicker
          api={api.admin}
          onSelect={(item) => setLineItems((prev) => [...prev, item])}
          onClose={() => setShowProductPicker(false)}
        />
      )}
    </div>
  );
}
